import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, HelpCircle } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/constants";
import { PageHeader, RoleBadge, EmptyState } from "@/components/Badges";
import { StatTile } from "@/components/training/TrainingBits";

const QuestionTable = ({ rows, testId }) => (
  <div className="overflow-hidden rounded-2xl border border-lasso-line bg-white">
    <Table data-testid={testId}>
      <TableHeader><TableRow><TableHead>Question</TableHead><TableHead>Asked by</TableHead><TableHead>Model</TableHead><TableHead>When</TableHead></TableRow></TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.id}>
            <TableCell className="max-w-md text-sm">
              {r.question}
              {r.not_covered && <span className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">not covered</span>}
              {r.error && <span className="ml-2 rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-semibold text-rose-700">error</span>}
            </TableCell>
            <TableCell><p className="text-sm">{r.user_name}</p>{r.user_role && <RoleBadge role={r.user_role} />}</TableCell>
            <TableCell className="text-xs text-muted-foreground">{r.model}</TableCell>
            <TableCell className="text-xs text-muted-foreground">{formatDate(r.created_at)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

export default function AiSettings() {
  const [settings, setSettings] = useState(null);
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    api.get("/admin/ai-settings").then((r) => setSettings(r.data));
    api.get("/admin/ai-usage").then((r) => setUsage(r.data));
  }, []);

  const choose = async (key) => {
    try {
      const { data } = await api.put("/admin/ai-settings", { model_key: key });
      setSettings(data);
      toast.success(`Ask Lasso and the SOP drafter now use ${data.options.find((o) => o.key === key).label}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="fade-up">
      <PageHeader eyebrow="Admin" title="AI assistant" description="Choose the model behind Ask Lasso and the SOP drafter, and see what staff are asking." />
      {settings && (
        <section className="mb-12">
          <h2 className="mb-4 font-display text-sm font-bold uppercase tracking-[0.14em]">Model</h2>
          <div className="grid gap-4 md:grid-cols-3" data-testid="ai-model-options">
            {settings.options.map((o) => {
              const active = settings.model_key === o.key;
              return (
                <button key={o.key} onClick={() => !active && choose(o.key)} data-testid={`ai-model-${o.key}`} className={`rounded-2xl border p-5 text-left transition-colors ${active ? "border-lasso-ink bg-lasso-ink text-white" : "border-lasso-line bg-white hover:border-lasso-ink"}`}>
                  <div className="flex items-center justify-between">
                    <p className="font-display text-base font-bold">{o.label}</p>
                    {active && <Check className="h-4 w-4 text-lasso-sky" />}
                  </div>
                  <p className={`mt-2 font-mono text-xs ${active ? "text-white/60" : "text-zinc-500"}`}>{o.provider} · {o.model}</p>
                  {o.key === settings.default && <p className={`mt-3 text-[10px] font-semibold uppercase tracking-[0.18em] ${active ? "text-lasso-sky" : "text-lasso-deep"}`}>Default</p>}
                </button>
              );
            })}
          </div>
          {settings.updated_by && <p className="mt-3 text-xs text-muted-foreground">Last changed by {settings.updated_by} on {formatDate(settings.updated_at)}</p>}
        </section>
      )}
      {usage && (
        <>
          <div className="mb-10 grid grid-cols-2 gap-4 lg:grid-cols-5">
            <StatTile label="Questions asked" value={usage.total_questions} testId="ai-usage-total" />
            <StatTile label="Last 7 days" value={usage.last_7_days} testId="ai-usage-week" />
            <StatTile label="Not covered" value={usage.not_covered} testId="ai-usage-not-covered" />
            <StatTile label="People" value={usage.unique_users} testId="ai-usage-users" />
            <StatTile label="Errors" value={usage.errors} tone="danger" testId="ai-usage-errors" />
          </div>
          <section className="mb-10">
            <h2 className="mb-2 flex items-center gap-2 font-display text-sm font-bold uppercase tracking-[0.14em]"><HelpCircle className="h-4 w-4 text-amber-600" /> Not covered by SOPs</h2>
            <p className="mb-4 text-sm text-muted-foreground">Questions staff asked that no SOP answers — candidates for new SOPs.</p>
            {usage.recent_not_covered.length === 0 ? <EmptyState title="No gaps found yet" testId="ai-not-covered-empty" /> : <QuestionTable rows={usage.recent_not_covered} testId="ai-not-covered-table" />}
          </section>
          <section>
            <h2 className="mb-4 font-display text-sm font-bold uppercase tracking-[0.14em]">Recent questions</h2>
            {usage.recent_questions.length === 0 ? <EmptyState title="No questions yet" /> : <QuestionTable rows={usage.recent_questions} testId="ai-recent-table" />}
          </section>
        </>
      )}
    </div>
  );
}
